import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormMessage } from "@/components/ui/form-message";
import { validateBanReason } from "@/features/admin/adminValidation";

type UnbanUserDialogProps = {
  busy?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: { reason: string }) => void;
  open: boolean;
};

export function UnbanUserDialog({ busy = false, onOpenChange, onSubmit, open }: UnbanUserDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  function handleSubmit() {
    const validationError = validateBanReason(reason);
    setError(validationError);

    if (validationError) {
      return;
    }

    onSubmit({ reason: reason.trim() });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("adminWorkspace.dialogs.unbanUser.title")}</DialogTitle>
          <DialogDescription>{t("adminWorkspace.dialogs.unbanUser.description")}</DialogDescription>
        </DialogHeader>
        <textarea
          className="min-h-28 w-full rounded-[1.75rem] border border-ink/10 bg-white/80 px-4 py-3 text-sm text-ink shadow-sm outline-none transition focus:border-clay/50 focus:ring-2 focus:ring-clay/20"
          placeholder={t("adminWorkspace.dialogs.unbanUser.reasonPlaceholder")}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? <FormMessage>{t(error)}</FormMessage> : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={busy}
            type="button"
            onClick={handleSubmit}
          >
            {busy ? t("common.saving") : t("adminWorkspace.dialogs.unbanUser.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
